import { Fragment, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Button, Card, Dropdown, Spinner, Table } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import SignedBillActions from "../Common/SignedBillActions";
import PortalMenu from "../Common/PortalMenu";

const money = (value) =>
 new Intl.NumberFormat("en-IN", {
 style: "currency",
 currency: "INR",
 }).format(Number(value || 0));

const statusVariant = (status) => {
 if (status === "Paid") return "success";
 if (status === "Partially Paid") return "warning";
 if (status === "Rejected") return "danger";
 return "info";
};

const getItemKey = (item) =>
 (item.boqItemId || item.wlino || item.itemName || "").toString();

// Vertical three-dot icon ⋮
const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
 <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
 <rect x="0" y="0" width="24" height="24" />
 <circle fill="#000000" cx="12" cy="5" r="2" />
 <circle fill="#000000" cx="12" cy="12" r="2" />
 <circle fill="#000000" cx="12" cy="19" r="2" />
 </g>
 </svg>
);

const ContractorBillingList = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const workOrderFromUrl = searchParams.get("workOrder") || "";
 const [billingRows, setBillingRows] = useState([]);
 const [loading, setLoading] = useState(true);
 const [downloadingPdf, setDownloadingPdf] = useState(null); // billId being downloaded
 const [previewingPdf, setPreviewingPdf] = useState(null); // billId being previewed

 const loadBillingQueue = async () => {
 try {
 setLoading(true);
 const cwoParams = new URLSearchParams({ status: "Approved" });
 const billParams = new URLSearchParams();
 if (workOrderFromUrl) {
 cwoParams.append("workOrder", workOrderFromUrl);
 billParams.append("workOrder", workOrderFromUrl);
 }

 const [cwoRes, billRes] = await Promise.all([
 api.get(`/contractual-work-orders?${cwoParams.toString()}`),
 api.get(`/contractor-billings?${billParams.toString()}`),
 ]);

 const approvedCwos = cwoRes.data.success ? cwoRes.data.data || [] : [];
 const bills = billRes.data.success ? billRes.data.data || [] : [];
 const billsByCwo = new Map();

 bills.forEach((bill) => {
 const cwoId = bill.contractualWorkOrder?._id || bill.contractualWorkOrder;
 if (cwoId) {
 const existingBills = billsByCwo.get(cwoId) || [];
 billsByCwo.set(cwoId, [...existingBills, bill]);
 }
 });

 const rows = [];

 approvedCwos.forEach((cwo) => {
 const cwoBills = billsByCwo.get(cwo._id) || [];
 if (cwoBills.length) {
 cwoBills.forEach((bill) => {
 rows.push({ id: bill._id, cwo, bill, bills: cwoBills });
 });
 } else {
 rows.push({ id: cwo._id, cwo, bill: null, bills: [] });
 }
 });

 const existingCwoIds = new Set(approvedCwos.map((cwo) => cwo._id));
 bills.forEach((bill) => {
 const cwo = bill.contractualWorkOrder;
 const cwoId = cwo?._id || cwo;
 if (cwoId && !existingCwoIds.has(cwoId)) {
 const cwoBills = billsByCwo.get(cwoId) || [bill];
 rows.push({
 id: bill._id,
 cwo: {
 _id: cwoId,
 cwoNo: cwo?.cwoNo,
 workOrder: bill.workOrder,
 vendor: bill.vendor,
 totalAmount: bill.totalAmount,
 status: cwo?.status,
 },
 bill,
 bills: cwoBills,
 });
 }
 });

 setBillingRows(rows);
 } catch (error) {
 swal("Error", "Failed to load contractor billing list", "error");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 loadBillingQueue();
 }, [workOrderFromUrl]);

 const getCreateBillUrl = (cwo) => {
 const workOrderId = cwo?.workOrder?._id || cwo?.workOrder || workOrderFromUrl;
 const params = new URLSearchParams();
 if (cwo?._id) params.append("cwo", cwo._id);
 if (workOrderId) params.append("workOrder", workOrderId);
 return `/contractor-billing/add?${params.toString()}`;
 };

 /* PDF Handlers */
 const handlePreviewPDF = async (billId) => {
 setPreviewingPdf(billId);
 const previewWindow = window.open("", "_blank");
 try {
 const res = await api.get(`/contractor-billings/${billId}/pdf`, {
 params: { mode: "preview" },
 responseType: "blob",
 timeout: 120000,
 suppressErrorToast: true,
 });

 const contentType = res.headers?.["content-type"] || "";
 if (!contentType.includes("application/pdf")) {
 if (previewWindow) previewWindow.close();
 swal("Error", "Failed to generate PDF preview", "error");
 return;
 }

 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );
 if (previewWindow) {
 previewWindow.location.href = fileURL;
 } else {
 window.open(fileURL, "_blank");
 }
 } catch (err) {
 if (previewWindow) previewWindow.close();
 swal("Error", err.response?.data?.message || "Failed to preview PDF", "error");
 } finally {
 setPreviewingPdf(null);
 }
 };

 const handleDownloadPDF = async (billId, billNo) => {
 setDownloadingPdf(billId);
 try {
 const res = await api.get(`/contractor-billings/${billId}/pdf`, {
 responseType: "blob",
 timeout: 120000,
 });

 const url = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );
 const link = document.createElement("a");
 link.href = url;
 link.download = `Contractor-Bill-${billNo || billId}.pdf`;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.URL.revokeObjectURL(url);
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to download PDF", "error");
 } finally {
 setDownloadingPdf(null);
 }
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Contractor Billing" motherMenu="Procurement" />
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">
 Contractor Billing
 {workOrderFromUrl && <Badge bg="secondary" className="ms-2">Work Order Filtered</Badge>}
 </Card.Title>
 <div className="d-flex gap-2">
 <Button variant="light" onClick={() => navigate(-1)}>Back</Button>
 </div>
 </Card.Header>
 <Card.Body>
 {loading ? (
 <div className="text-center py-5"><Spinner /></div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th>CWO</th>
 <th>Work Order</th>
 <th>Vendor</th>
 <th>CWO Total</th>
 <th>Bill No</th>
 <th>Paid</th>
 <th>Remaining</th>
 <th>Billing Status</th>
 <th>Signed Bill</th>
 <th className="text-end">Actions</th>
 </tr>
 </thead>
 <tbody>
 {billingRows.map(({ id, cwo, bill, bills = [] }) => {
 const total = Number(bill?.totalAmount ?? cwo?.totalAmount ?? 0);
 const paid = Number(bill?.paidAmount || 0);
 const remaining = Math.max(total - paid, 0);

 const billedQuantityByItem = new Map();
 bills
 .filter((b) => b.status !== "Rejected")
 .forEach((b) => {
 (b.items || []).forEach((item) => {
 const k = getItemKey(item);
 billedQuantityByItem.set(k, Number(billedQuantityByItem.get(k) || 0) + Number(item.quantity || 0));
 });
 });

 const hasRemainingQuantity = (cwo?.selectedBoqItems || []).some(
 (item) =>
 Number(item.quantity || 0) >
 Number(billedQuantityByItem.get(getItemKey(item)) || 0)
 );

 const latestBillId = bills[0]?._id;
 const showCreateNextBill = hasRemainingQuantity && (!bill || bill._id === latestBillId);

 return (
 <tr key={id}>
 <td><strong>{cwo?.cwoNo || "-"}</strong></td>
 <td>{cwo?.workOrder?.woNo || bill?.workOrder?.woNo || "-"}</td>
 <td>{cwo?.vendor?.name || bill?.vendor?.name || "-"}</td>
 <td>{money(total)}</td>
 <td>
 {bill ? (
 <>
 <strong>{bill.billNo}</strong>
 <div className="small text-muted">{bill.invoiceNo || ""}</div>
 </>
 ) : (
 <span className="text-muted">Not created</span>
 )}
 </td>
 <td>{money(paid)}</td>
 <td>{money(remaining)}</td>
 <td>
 {bill ? (
 <Badge bg={statusVariant(bill.status)}>{bill.status}</Badge>
 ) : (
 <Badge bg="secondary">Bill Pending</Badge>
 )}
 </td>
 <td>
 {bill ? (
 <div className="d-flex flex-column gap-1">
 <div className="small text-muted">
 {bill.signedCopy?.uploadedAt
 ? new Date(bill.signedCopy.uploadedAt).toLocaleDateString("en-IN")
 : "Not uploaded"}
 </div>
 <SignedBillActions
 id={bill._id}
 type="contractor-billings"
 signedCopy={bill.signedCopy}
 onUploadSuccess={loadBillingQueue}
 />
 </div>
 ) : (
 <span className="text-muted">-</span>
 )}
 </td>
 <td className="text-end">
 {bill ? (
 <div className="d-flex justify-content-end align-items-center gap-2">
 {showCreateNextBill && (
 <Button
 size="sm"
 variant="outline-primary"
 onClick={() => navigate(getCreateBillUrl(cwo))}
 >
 Create Next Bill
 </Button>
 )}

 {/* Three-dot dropdown */}
 <Dropdown drop="start">
 <Dropdown.Toggle
 as="div"
 bsPrefix="dropdown-toggle-no-caret"
 style={{ cursor: "pointer", lineHeight: 1, display: "inline-block" }}
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <Dropdown.Item
 onClick={() => navigate(`/contractor-billing/view/${bill._id}`)}
 >
 <i className="fas fa-eye me-2 text-primary" />
 View
 </Dropdown.Item>

 <Dropdown.Divider />

 <Dropdown.Item
 disabled={previewingPdf === bill._id}
 onClick={() => handlePreviewPDF(bill._id)}
 >
 <i className="fas fa-eye me-2 text-info" />
 {previewingPdf === bill._id ? "Loading..." : "Preview PDF"}
 </Dropdown.Item>

 <Dropdown.Item
 disabled={downloadingPdf === bill._id}
 onClick={() => handleDownloadPDF(bill._id, bill.billNo)}
 >
 <i className="fas fa-file-pdf me-2 text-danger" />
 {downloadingPdf === bill._id ? "Downloading..." : "Download PDF"}
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </div>
 ) : (
 <Button size="sm" onClick={() => navigate(getCreateBillUrl(cwo))}>
 Create Bill
 </Button>
 )}
 </td>
 </tr>
 );
 })}
 {billingRows.length === 0 && (
 <tr>
 <td colSpan="10" className="text-center text-muted py-5">
 No approved contractual work orders found.
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>
 </Fragment>
 );
};

export default ContractorBillingList;
